import { useEffect, useState } from "react";

/**
 * Resolved theme as currently applied to ``<html>``.
 *
 * The toggle in :mod:`components/ThemeToggle` flips the
 * ``theme-light`` class on ``<html>`` based on the user preference
 * ({@link ThemeChoice}). Widgets that need to react to the change
 * (chart strokes, map styles, …) read it through this hook so a
 * change in the toggle re-renders them without forcing a reload.
 */
export type ResolvedTheme = "light" | "dark";

/** The three options the user can pick from. */
export type ThemeChoice = "light" | "dark" | "auto";

const STORAGE_KEY = "argos.theme";
const LIGHT_CLASS = "theme-light";

function systemPrefersDark(): boolean {
  if (typeof window === "undefined") return true;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? true;
}

export function applyTheme(choice: ThemeChoice): ResolvedTheme {
  const root = document.documentElement;
  const resolved: ResolvedTheme =
    choice === "auto" ? (systemPrefersDark() ? "dark" : "light") : choice;
  if (resolved === "light") {
    root.classList.add(LIGHT_CLASS);
  } else {
    root.classList.remove(LIGHT_CLASS);
  }
  return resolved;
}

export function readStoredTheme(): ThemeChoice {
  if (typeof window === "undefined") return "auto";
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === "light" || raw === "dark" || raw === "auto") return raw;
  return "auto";
}

export function storeTheme(choice: ThemeChoice): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, choice);
}

/**
 * Hook for components that only need to know the resolved theme
 * (light or dark) at render time. Subscribes to mutations on
 * ``<html>``'s ``class`` attribute so widgets re-render when the
 * toggle flips the value.
 */
export function useTheme(): ResolvedTheme {
  const [theme, setTheme] = useState<ResolvedTheme>(() =>
    typeof document !== "undefined" &&
    document.documentElement.classList.contains(LIGHT_CLASS)
      ? "light"
      : "dark"
  );

  useEffect(() => {
    const target = document.documentElement;
    const observer = new MutationObserver(() => {
      setTheme(target.classList.contains(LIGHT_CLASS) ? "light" : "dark");
    });
    observer.observe(target, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  return theme;
}
